// frontend/src/hooks/useInterviewSession.js
import { trackSuccess } from "../utils/siteAnalytics";
import { useState, useRef, useCallback, useEffect } from "react";

const POLL_START_DELAY_MS  = 2000;
const POLL_INTERVAL_MS     = 3000;
const RETRY_DELAYS_MS      = [2000, 4000];
const RECOVERY_POLL_MS     = 10000;  // check every 10s during recovery
const RECOVERY_MAX_MS      = 420000; // give up 7 min after entering recovery
const CLIENT_SYSTEM_TOKEN_ALLOWANCE = 10000;
const MIN_TURN_TIMEOUT_MS  = 120000;
const MAX_TURN_TIMEOUT_MS  = 300000;
const SERVER_GRACE_MS      = 5000;

function pendingTurnKey(interviewId) {
  return `prime-portfolio-pending-turn:${interviewId}`;
}

export function readPendingTurn(interviewId) {
  try {
    const parsed = JSON.parse(localStorage.getItem(pendingTurnKey(interviewId)) || "null");
    return parsed?.requestId && parsed?.text ? parsed : null;
  } catch {
    return null;
  }
}

export function writePendingTurn(interviewId, pending) {
  try { localStorage.setItem(pendingTurnKey(interviewId), JSON.stringify(pending)); } catch { /* storage can be unavailable */ }
}

export function clearPendingTurn(interviewId) {
  try { localStorage.removeItem(pendingTurnKey(interviewId)); } catch { /* storage can be unavailable */ }
}

export function estimateTurnTimeoutMs(messages = [], text = "") {
  const transcriptCharacters = messages.reduce((total, message) => total + (message?.content?.length || 0), text.length);
  const estimatedTokens = CLIENT_SYSTEM_TOKEN_ALLOWANCE + Math.ceil(transcriptCharacters / 4);
  const calculatedSeconds = 90 + 2 * Math.ceil(estimatedTokens / 1000);
  return Math.max(MIN_TURN_TIMEOUT_MS, Math.min(MAX_TURN_TIMEOUT_MS, calculatedSeconds * 1000));
}

export function useInterviewSession({ interviewId, authClient, onReceived, onError }) {
  const [phase, setPhase] = useState("idle");
  const [elapsed, setElapsed] = useState(0);

  // Keep latest callbacks in refs so stale closures never call old versions
  const onReceivedRef = useRef(onReceived);
  const onErrorRef    = useRef(onError);
  useEffect(() => { onReceivedRef.current = onReceived; }, [onReceived]);
  useEffect(() => { onErrorRef.current    = onError;    }, [onError]);

  // Internal refs — cleared on each cleanup()
  const mountedRef           = useRef(true);
  const resolvedRef          = useRef(false);
  const abortControllerRef   = useRef(null);
  const pollIntervalRef      = useRef(null);
  const pollStartTimerRef    = useRef(null);
  const elapsedTimerRef      = useRef(null);
  const maxWaitTimerRef      = useRef(null);
  const recoveryIntervalRef  = useRef(null);
  const recoveryMaxTimerRef  = useRef(null);
  const baselineCountRef     = useRef(0);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      cleanupTimers();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const cleanupTimers = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    clearInterval(pollIntervalRef.current);
    clearTimeout(pollStartTimerRef.current);
    clearInterval(elapsedTimerRef.current);
    clearTimeout(maxWaitTimerRef.current);
    clearInterval(recoveryIntervalRef.current);
    clearTimeout(recoveryMaxTimerRef.current);
    pollIntervalRef.current      = null;
    pollStartTimerRef.current    = null;
    elapsedTimerRef.current      = null;
    maxWaitTimerRef.current      = null;
    recoveryIntervalRef.current  = null;
    recoveryMaxTimerRef.current  = null;
  }, []);

  const resolve = useCallback((data) => {
    if (resolvedRef.current || !mountedRef.current) return;
    resolvedRef.current = true;
    cleanupTimers();
    clearPendingTurn(interviewId);
    setPhase("received");
    if (data.status === "complete") trackSuccess("interview_completed", interviewId);
    onReceivedRef.current(data);
  }, [cleanupTimers, interviewId]);

  const fail = useCallback((message) => {
    if (resolvedRef.current || !mountedRef.current) return;
    resolvedRef.current = true;
    cleanupTimers();
    setPhase("error");
    onErrorRef.current(message);
  }, [cleanupTimers]);

  // After the initial timeout, keep polling silently for RECOVERY_MAX_MS more.
  // If the LLM eventually responds it will be caught here and shown normally.
  const startRecovery = useCallback(() => {
    if (resolvedRef.current || !mountedRef.current) return;
    setPhase("recovering");

    recoveryIntervalRef.current = setInterval(async () => {
      if (resolvedRef.current) return;
      try {
        const { data } = await authClient.get(`/interviews/${interviewId}`);
        const count = (data.messages || []).filter(m => m.role === "assistant").length;
        if (count > baselineCountRef.current) resolve(data);
      } catch {
        // recovery poll errors are silent
      }
    }, RECOVERY_POLL_MS);

    recoveryMaxTimerRef.current = setTimeout(() => {
      fail("The intelligence layer did not respond in time. Your answer is saved — try again, or start a new interview.");
    }, RECOVERY_MAX_MS);
  }, [authClient, interviewId, resolve, fail]);

  const startPolling = useCallback(() => {
    pollIntervalRef.current = setInterval(async () => {
      if (resolvedRef.current) return;
      try {
        const { data } = await authClient.get(`/interviews/${interviewId}`);
        const count = (data.messages || []).filter(m => m.role === "assistant").length;
        if (count > baselineCountRef.current) resolve(data);
      } catch {
        // poll errors are silent — keep trying
      }
    }, POLL_INTERVAL_MS);
  }, [authClient, interviewId, resolve]);

  const send = useCallback(async (text, baselineCount, messages = []) => {
    cleanupTimers();
    resolvedRef.current      = false;
    baselineCountRef.current = baselineCount;

    if (mountedRef.current) {
      setPhase("submitting");
      setElapsed(0);
    }

    // Elapsed timer — ticks every second
    elapsedTimerRef.current = setInterval(() => {
      if (mountedRef.current) setElapsed(e => e + 1);
    }, 1000);

    const maxWaitMs = estimateTurnTimeoutMs(messages, text) + SERVER_GRACE_MS;

    // After the token-informed server budget switch to recovery mode.
    maxWaitTimerRef.current = setTimeout(() => {
      // Stop the fast poll and elapsed timer; recovery uses its own slower poll
      clearInterval(pollIntervalRef.current);
      clearInterval(elapsedTimerRef.current);
      pollIntervalRef.current  = null;
      elapsedTimerRef.current  = null;
      startRecovery();
    }, maxWaitMs);

    // Start polling after a short delay regardless of POST status
    pollStartTimerRef.current = setTimeout(() => {
      if (!resolvedRef.current) {
        if (mountedRef.current) setPhase("waiting");
        startPolling();
      }
    }, POLL_START_DELAY_MS);

    // POST with up to 3 attempts (1 original + 2 retries)
    const previousPending = readPendingTurn(interviewId);
    const requestId = previousPending?.text === text
      ? previousPending.requestId
      : (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`);
    writePendingTurn(interviewId, {
      requestId,
      text,
      submittedAt: previousPending?.text === text ? previousPending.submittedAt : Date.now(),
      baselineAssistantCount: baselineCount,
      timeoutMs: maxWaitMs - SERVER_GRACE_MS,
      persistence: previousPending?.persistence || "local",
    });

    let attempts = 0;
    const maxAttempts = 1 + RETRY_DELAYS_MS.length;

    const attemptPost = async () => {
      if (resolvedRef.current) return;
      attempts++;

      abortControllerRef.current = new AbortController();
      try {
        const { data } = await authClient.post(
          `/interviews/${interviewId}/messages`,
          { content: text, request_id: requestId },
          { signal: abortControllerRef.current.signal }
        );

        if (resolvedRef.current) return;

        // Fast path: POST response already contains the LLM reply
        const count = (data.messages || []).filter(m => m.role === "assistant").length;
        if (count > baselineCountRef.current) {
          resolve(data);
          return;
        }

        // POST succeeded but LLM is still thinking — polling takes over
        writePendingTurn(interviewId, {
          ...readPendingTurn(interviewId),
          requestId,
          text,
          baselineAssistantCount: baselineCount,
          persistence: "server",
        });
        if (mountedRef.current) setPhase("waiting");

      } catch (err) {
        if (err.name === "AbortError" || err.name === "CanceledError") return;
        if (resolvedRef.current) return;

        // 400 "already complete" means the server processed our message and finished
        // the interview (common when a proxy timeout cut the original connection).
        // Fetch the completed interview immediately instead of retrying.
        if (err.response?.status === 400 && err.response?.data?.detail?.includes("already complete")) {
          try {
            const { data } = await authClient.get(`/interviews/${interviewId}`);
            resolve(data);
          } catch {
            // If the fetch also fails, fall through to polling
          }
          return;
        }

        if (attempts < maxAttempts) {
          // Wait then retry with the same requestId (backend deduplicates safely)
          await new Promise(r => setTimeout(r, RETRY_DELAYS_MS[attempts - 1]));
          await attemptPost();
        }
        // If all retries exhausted, polling + maxWait handle the eventual outcome
      }
    };

    await attemptPost();
  }, [authClient, interviewId, resolve, startPolling, startRecovery, cleanupTimers]);

  // Reconnect to an in-flight turn after reload without re-posting or changing
  // its request id. If the server never received it, the existing draft remains.
  useEffect(() => {
    const pending = readPendingTurn(interviewId);
    if (!pending) return undefined;

    let active = true;
    authClient.get(`/interviews/${interviewId}`).then(({ data }) => {
      if (!active || resolvedRef.current) return;
      const messages = data.messages || [];
      const assistantCount = messages.filter(message => message.role === "assistant").length;
      if (assistantCount > (pending.baselineAssistantCount || 0)) {
        resolve(data);
        return;
      }
      const answerIsStored = messages.some(message => message.role === "user" && message.content?.trim() === pending.text.trim());
      if (answerIsStored) {
        baselineCountRef.current = pending.baselineAssistantCount || assistantCount;
        setPhase("recovering");
        startRecovery();
      } else {
        clearPendingTurn(interviewId);
      }
    }).catch(() => {
      // Keep the pending record and browser draft for the next retry/reload.
    });

    return () => { active = false; };
  }, [authClient, interviewId, resolve, startRecovery]);

  const cancel = useCallback(() => {
    resolvedRef.current = true; // prevent any pending callbacks from firing
    cleanupTimers();
    if (mountedRef.current) {
      setPhase("idle");
      setElapsed(0);
    }
  }, [cleanupTimers]);

  return { phase, elapsed, send, cancel };
}
